<template>
  <v-dialog persistent :value="value" width="700" style="transform-origin: center top" @input="$emit('input', $event)">
    <v-card relative>
      <v-card-title class="headline">{{ editItem ? 'Update content ads' : 'Up content ads' }}</v-card-title>
      <div class="close-icon" @click="close"><v-icon>fas fa-times</v-icon></div>
      <v-divider class="mb-3"></v-divider>
      <v-card-text>
        <!-- Upload area -->
        <div class="mb-4">
          <div class="font-weight-bold mb-2">Attachments (image / video)</div>
          <div class="upload-ads-area" @click="$refs.adsFileUploader.click()">
            <v-icon large color="primary">mdi-cloud-upload</v-icon>
            <div class="mt-1 grey--text">Click to upload images or videos</div>
          </div>
          <input
            ref="adsFileUploader"
            type="file"
            multiple
            accept="image/*,video/*"
            class="myfile"
            @change="onUploadFile"
          />
          <div v-if="uploadingCount > 0" class="mt-2 d-flex align-center" style="gap:8px">
            <v-progress-circular indeterminate color="primary" size="18"></v-progress-circular>
            <span class="grey--text">Uploading {{ uploadingCount }} file(s)...</span>
          </div>
          <div v-if="attachments.length > 0" class="mt-3 d-flex flex-wrap" style="gap:8px">
            <div
              v-for="(url, i) in attachments"
              :key="i"
              style="position:relative; width:80px; height:80px; border-radius:6px; overflow:hidden; border:1px solid #e0e0e0;"
            >
              <video
                v-if="isVideo(url)"
                :src="imageHelpers.url(url)"
                style="width:100%; height:100%; object-fit:cover; display:block;"
              />
              <v-img v-else :src="imageHelpers.url(url, { width: 160, height: 160 })" width="80" height="80" contain />
              <v-btn
                x-small
                icon
                color="white"
                style="position:absolute; top:2px; right:2px; background:rgba(0,0,0,0.45);"
                @click="attachments.splice(i, 1)"
              >
                <v-icon x-small>mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
        <!-- Content -->
        <div>
          <div class="font-weight-bold mb-2">Content</div>
          <v-textarea
            v-model="content"
            outlined
            rows="4"
            placeholder="Enter your content here..."
            hide-details
          ></v-textarea>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="close">Cancel</v-btn>
        <v-btn color="primary" :loading="isLoading" @click="submit">Submit</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { productApi } from '@/apis/product';
import { uploadService } from '@/apis/upload.s';
import imageHelpers from '@/helpers/image';
import axios from 'axios';

export default {
  name: 'UpContentAdsDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    productId: {
      type: String,
      default: null,
    },
    editItem: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      imageHelpers,
      content: '',
      attachments: [],
      isLoading: false,
      uploadingCount: 0,
    };
  },
  watch: {
    value(val) {
      if (val) {
        // pre-fill khi mở dialog edit
        this.content = this.editItem ? this.editItem.prompt || '' : '';
        this.attachments = this.editItem ? [...(this.editItem.resource_input || [])] : [];
      }
    },
  },
  methods: {
    isVideo(url) {
      if (!url) return false;
      return /\.(mp4|webm|ogg|mov|avi)(\?|$)/i.test(url);
    },
    close() {
      this.content = '';
      this.attachments = [];
      this.$emit('input', false);
    },
    async onUploadFile(e) {
      const files = Array.from(e.target.files);
      if (!files.length) return;
      this.uploadingCount += files.length;
      await Promise.all(
        files.map(async file => {
          try {
            const ext = file.name.split('.').pop();
            const id =
              Math.floor(Date.now() / 1000).toString(16) +
              ' '.repeat(16).replace(/./g, () => Math.floor(Math.random() * 16).toString(16));
            const urlData = await uploadService.get({ filename: `ads-${id}.${ext}` });
            if (urlData) {
              await axios.put(urlData.data.presignedUrl, file, { headers: { 'Content-Type': file.type } });
              this.attachments.push(urlData.data.imagePath);
            }
          } catch {
            this.$store.commit('setMessages', { messages: 'Upload failed: ' + file.name, type: 'error' });
          } finally {
            this.uploadingCount--;
          }
        }),
      );
      this.$refs.adsFileUploader.value = '';
    },
    async submit() {
      try {
        this.isLoading = true;
        let res;
        if (this.editItem) {
          res = await productApi.sharePromtUpdate(this.editItem._id, {
            attachments: this.attachments,
            content: this.content,
          });
        } else {
          res = await productApi.sharePromtCreate({
            attachments: this.attachments,
            content: this.content,
            productId: this.productId,
          });
        }
        if (res && res.data && res.data.status === false) {
          this.$store.commit('setMessages', { messages: res.data.message || 'Failed to save.', type: 'error' });
          return;
        }
        this.$store.commit('setMessages', { messages: 'Saved successfully!', type: 'success' });
        this.$emit('saved');
        this.close();
      } catch {
        this.$store.commit('setMessages', { messages: 'Failed to save.', type: 'error' });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss">
.upload-ads-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px dashed #4181cb;
  border-radius: 6px;
  min-height: 100px;
  cursor: pointer;
  background: #fafafa;
  &:hover {
    background: #f0f4ff;
  }
}
</style>
